import { ListLayout } from "client/Features/Hub/ListLayout/ListLayout";
import { ReactElement, useMemo } from "react";
import {
	GroupOverviewCardItem,
	OverviewItem,
	OverviewItemKind
} from "./GroupOverviewCardItem";
import { generateTrackingId } from "client/Misc/utils";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { GroupOverviewListItem } from "./GroupOverviewListItem";
import {
	getKnowledgeItemCategoryImageUrl,
	getKnowledgeItemGroupImageUrl
} from "client/Features/Hub/common";

export type ListProps = {
	items: readonly OverviewItem[];
	onClickItem: (item: OverviewItem) => void;
};

export const List = (props: ListProps): ReactElement => {
	const {
		items,
		onClickItem
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	return (
		<ListLayout
			trackingId="knowledge-items-group-view"
			renderAfterItems={<ListLayout.MobilePlusMenuSpacing />}
			hasBorderTop={isMobile}
		>
			{items.map(
				x => (
					<ListLayout.Item
						key={x.uniqueKey}
					>
						<Item
							item={x}
							isMobile={isMobile}
							onClick={() => onClickItem(x)}
							trackingId={generateTrackingId("group-overview-item", `|${x.uniqueKey}`)}
						/>
					</ListLayout.Item>
				)
			)}
		</ListLayout>
	);
};
List.displayName = "List";

export type ItemProps = {
	item: OverviewItem;
	isMobile: boolean;
	onClick: () => void;
	trackingId: string;
};

export const Item = (props: ItemProps): ReactElement => {
	const {
		item,
		isMobile,
		onClick,
		trackingId
	} = props;

	const { description, imageUrl } = useMemo(
		(): DetailedInfo => getDetailedInfo(item),
		[item]
	);

	return isMobile
		? (
			<GroupOverviewListItem
				title={item.name}
				hasNewItems={item.newItemsCount > 0}
				description={description}
				imageUrl={imageUrl}
				onClick={onClick}
				trackingId={trackingId}
			/>
		)
		: (
			<GroupOverviewCardItem
				title={item.name}
				newItemsCount={item.newItemsCount}
				description={description}
				imageUrl={imageUrl}
				trackingId={trackingId}
				onClick={onClick}
			/>
		);
};
Item.displayName = "Item";

type DetailedInfo = {
	description: string | null;
	imageUrl: string | null;
};

function getDetailedInfo(
	item: OverviewItem
): DetailedInfo {
	const { state } = item;

	if (state.status !== "ready") {
		return { description: null, imageUrl: null };
	}

	const { imageId, description } = state.details;

	return {
		description,
		imageUrl: imageId.isSet
			? itemKindToImageUrlFactor[item.kind](
				imageId.value
			)
			: null
	};
}

const itemKindToImageUrlFactor: Record<OverviewItemKind, (id: string) => string> = {
	[OverviewItemKind.Category]: getKnowledgeItemCategoryImageUrl,
	[OverviewItemKind.Group]: getKnowledgeItemGroupImageUrl
};
